<template>
  <v-card flat class="pa-4">
    <v-card-title class="text-h5 font-weight-bold mb-2">
      Tell us about your business
    </v-card-title>
    <v-card-subtitle class="mb-6">
      Step 1 of 3 — We'll use this to generate your landing page
    </v-card-subtitle>

    <v-card-text>
      <p class="text-subtitle-1 font-weight-medium mb-3">What is this landing page about?</p>
      <v-item-group
        v-model="pageType"
        mandatory
        class="d-flex mb-6"
      >
        <v-item v-slot="{ active, toggle }" value="product">
          <v-card
            :color="active ? 'primary' : 'grey lighten-4'"
            :dark="active"
            class="d-flex align-center px-6 py-3 mr-4 customBorderRadius cursor-pointer transition-swing"
            flat
            @click="toggle"
          >
            <v-icon left :color="active ? 'white' : 'grey darken-2'" class="mr-2">mdi-package-variant</v-icon>
            <span class="font-weight-medium">A Product</span>
          </v-card>
        </v-item>
        <v-item v-slot="{ active, toggle }" value="service">
          <v-card
            :color="active ? 'primary' : 'grey lighten-4'"
            :dark="active"
            class="d-flex align-center px-6 py-3 customBorderRadius cursor-pointer transition-swing"
            flat
            @click="toggle"
          >
            <v-icon left :color="active ? 'white' : 'grey darken-2'" class="mr-2">mdi-briefcase-outline</v-icon>
            <span class="font-weight-medium">A Service</span>
          </v-card>
        </v-item>
      </v-item-group>

      <v-text-field v-model="businessName"
        :label="pageType === 'product' ? 'Product Name *' : 'Business Name *'"
        :placeholder="pageType === 'product' ? 'e.g. Acme Smartwatch' : 'e.g. Acme Real Estate'"
        outlined
        dense
        :rules="[rules.required]"
        class="customBorderRadius mb-4"
      />

      <v-text-field v-model="businessWebsite"
        label="Website URL (Optional)"
        placeholder="https://www.yourwebsite.com"
        outlined
        :rules="[rules.url]"
        hide-details="auto"
        class="mb-6 customBorderRadius"
        hint="If you provide a URL, we will automatically extract features, descriptions, and images to save you time!"
        persistent-hint
      >
        <template v-slot:prepend-inner>
          <v-icon color="grey" class="mr-2">mdi-link</v-icon>
        </template>
      </v-text-field>

      <v-autocomplete v-model="selectedListId"
        :items="lists"
        item-text="name"
        item-value="id"
        label="Select a GoDial List *"
        placeholder="Choose the list where leads will be captured"
        outlined
        dense
        :loading="loadingLists"
        :rules="[rules.required]"
        no-data-text="No lists found"
        class="customBorderRadius mb-6"
      />
    </v-card-text>

    <v-card-actions class="px-4 pb-4">
      <v-spacer />
      <v-btn class="customBorderRadius"
        color="primary"
        :disabled="!isValid || extracting"
        :loading="extracting"
        @click="next"
      >
        <template v-if="extracting">
          <v-icon left small class="mr-1">mdi-web</v-icon>
          Scanning website…
        </template>
        <template v-else>
          Next
          <v-icon right>mdi-arrow-right</v-icon>
        </template>
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import _ from "lodash";

export default {
  name: "WizardStep1",
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      pageType: this.value.pageType || "product",
      businessName: this.value.businessName || "",
      businessWebsite: this.value.businessWebsite || "",
      selectedListId: this.value.listsId || null,
      lists: [],
      loadingLists: false,
      extracting: false,
      rules: {
        required: (v) => !!v || "This field is required",
        url: (v) => {
          if (!v) return true;
          return /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(v) || "Please enter a valid website URL";
        }
      },
    };
  },
  computed: {
    ...mapGetters(["ENDPOINT", "teams", "selectedTeam"]),
    companyDetails() {
      return this.$store.state.companyDetails;
    },
    isValid() {
      return (
        this.businessName.trim() !== "" &&
        this.selectedListId &&
        this.rules.url(this.businessWebsite) === true
      );
    },
  },
  mounted() {
    this.fetchLists();
  },
  methods: {
    getTeamIds() {
      var teams = [];
      if (this.selectedTeam == "allTeams") {
        teams = _.map(this.teams, "id");
      } else {
        teams.push(this.selectedTeam);
      }
      teams = _.without(teams, "allTeams");
      return teams;
    },
    async fetchLists() {
      this.loadingLists = true;
      try {
        var teamIds = this.getTeamIds();
        var res = await this.$http.post(`${this.ENDPOINT}/lists/fetch`, {
          teams: teamIds,
        });
        this.lists = res.body;
      } catch (e) {
        console.log(e);
      } finally {
        this.loadingLists = false;
      }
    },
    async next() {
      if (!this.isValid) return;

      let extractedData = {};
      let hasUrl = this.businessWebsite && this.businessWebsite.trim() !== '';

      if (hasUrl) {
        this.extracting = true;
        try {
          const res = await this.$http.post(`${this.ENDPOINT}/gopages/extractInfo`, { url: this.businessWebsite.trim() });
          if (res.body && res.body.data) {
            extractedData = res.body.data;
          } else if (res.body) {
            extractedData = res.body;
          }
        } catch (e) {
          console.error("Could not extract info", e);
          if (this.$swal) {
            this.$swal('Notice', 'Could not seamlessly extract data from that URL. Proceeding with blank fields for you to fill.', 'warning');
          }
        } finally {
          this.extracting = false;
        }
      }

      const content = this.value.pageContent || {};
      
      this.$emit("input", {
        ...this.value,
        pageType: this.pageType,
        businessName: this.businessName.trim(),
        businessWebsite: this.businessWebsite.trim(),
        businessDescription: extractedData.description || this.value.businessDescription || "",
        listsId: this.selectedListId,
        pageContent: {
          ...content,
          usps: extractedData.usps || content.usps || [],
          features: extractedData.features || content.features || [],
          faq: extractedData.faq || content.faq || [],
        }
      });
      
      this.$emit("next");
    },
  },
};
</script>